<!-- eslint-disable max-lines -->
<template>
	<sliding-sheet
		:is-open="isOpen"
		:title="fileName"
		width="700px"
		@close="$emit('close')"
	>
		<template #content>
			<!-- Loading State -->
			<v-skeleton-loader
				v-if="loading"
				type="card, card, card"
			/>

			<!-- Error State -->
			<v-alert
				v-else-if="error"
				type="error"
				class="ma-4"
			>
				<div class="d-flex flex-column align-center">
					<div class="mb-2">
						Failed to load file details
					</div>
					<v-btn
						small
						outlined
						@click="loadFileDetails"
					>
						Retry
					</v-btn>
				</div>
			</v-alert>

			<!-- Content -->
			<div v-else-if="fileDetails">
				<!-- File Summary Section -->
				<v-card
					outlined
					class="ma-4"
				>
					<v-card-title class="text-h6">
						File Summary
					</v-card-title>
					<v-card-text>
						<v-row>
							<v-col
								cols="6"
								class="pb-0"
							>
								<div class="text-caption text--secondary">
									File Type
								</div>
								<div class="text-body-1 font-weight-medium">
									{{ fileDetails.fileType }}
								</div>
							</v-col>
							<v-col
								cols="6"
								class="pb-0"
							>
								<div class="text-caption text--secondary">
									Status
								</div>
								<div class="mt-1">
									<status-badge
										:status="derivedStatus.label"
										:type="derivedStatus.type"
									/>
								</div>
							</v-col>
							<v-col
								cols="6"
								class="pb-0"
							>
								<div class="d-flex align-center">
									<div class="text-caption text--secondary">
										Duration
									</div>
									<v-tooltip
										bottom
										max-width="380"
									>
										<template #activator="{ on, attrs }">
											<v-icon
												v-bind="attrs"
												x-small
												class="ml-1"
												color="grey"
												v-on="on"
											>
												mdi-information-outline
											</v-icon>
										</template>
										<span>
											Time taken to load the file into the system only. This does not include downstream data processing time.
										</span>
									</v-tooltip>
								</div>
								<div class="text-body-1 font-weight-medium">
									{{ formatDuration(fileDetails.durationSeconds) }}
								</div>
							</v-col>
							<v-col
								cols="6"
								class="pb-0"
							>
								<div class="text-caption text--secondary">
									Rows Accepted
								</div>
								<div class="text-body-1 font-weight-medium success--text">
									{{ fileDetails.rowsAccepted.toLocaleString() }}
								</div>
							</v-col>
							<v-col
								cols="6"
								class="pb-0"
							>
								<div class="text-caption text--secondary">
									Rows Rejected
								</div>
								<div class="text-body-1 font-weight-medium error--text">
									{{ fileDetails.rowsRejected.toLocaleString() }}
								</div>
							</v-col>
							<v-col
								cols="6"
								class="pb-0"
							>
								<div class="d-flex align-center">
									<div class="text-caption text--secondary">
										Submissions Created
									</div>
									<v-tooltip bottom>
										<template #activator="{ on, attrs }">
											<v-icon
												v-bind="attrs"
												x-small
												class="ml-1"
												color="grey"
												v-on="on"
											>
												mdi-information-outline
											</v-icon>
										</template>
										<span>
											Where multiple rows relate to the same individual, they are grouped into a single submission.
											This figure may differ from the total Rows Accepted.
										</span>
									</v-tooltip>
								</div>
								<div class="text-body-1 font-weight-medium">
									{{ fileDetails.submissionsCreated.toLocaleString() }}
								</div>
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>

				<!-- Rejects File Banner -->
				<v-alert
					v-if="hasRejections"
					type="warning"
					outlined
					class="ma-4"
				>
					<div class="text-body-2 font-weight-bold mb-2">
						A Rejects file has been generated for this upload
					</div>
					<div class="text-body-2">
						The rows that failed validation have been written to a Rejects file
						which is available for retrieval from your preconfigured sFTP location.
					</div>
					<div class="text-body-2 mt-2">
						The file will be named with a
						<code class="rejects-filename-suffix">
							_Rejects_YYYYMMDD_HHMM
						</code>
						suffix and can be used to correct and re-submit the affected records.
					</div>
				</v-alert>

				<!-- Rejection Breakdown Section -->
				<v-card
					v-if="hasRejections && hasRejectionDetails"
					outlined
					class="ma-4"
				>
					<v-card-title class="d-flex justify-space-between align-center">
						<span class="text-h6">
							Rejection Breakdown
						</span>
						<span class="text-caption text--secondary">
							{{ uniqueErrorCount }} unique error {{ uniqueErrorCount === 1 ? 'type' : 'types' }}
						</span>
					</v-card-title>
					<v-card-text class="pt-0">
						<div class="text-caption text--secondary font-italic mb-3">
							Percentages shown as a proportion of the rejected records
						</div>
						<v-expansion-panels class="rejection-panels">
							<v-expansion-panel
								v-for="(rejection, index) in visibleRejections"
								:key="index"
							>
								<v-expansion-panel-header :disable-icon-rotate="!canSeePiiData">
									<div class="d-flex justify-space-between align-center">
										<div class="flex-grow-1">
											<div class="text-body-2 font-weight-medium">
												{{ rejection.rejectionReason }}
											</div>
											<div class="text-caption text--secondary mt-1">
												<span class="error--text font-weight-medium">
													{{ rejection.rejectionCount.toLocaleString() }} records
												</span>
												<span class="mx-1">
													·
												</span>
												<span>
													{{ rejection.rejectionPercentage.toFixed(2) }}%
												</span>
											</div>
										</div>
										<v-tooltip
											v-if="!canSeePiiData"
											bottom
										>
											<template #activator="{ on, attrs }">
												<v-icon
													v-bind="attrs"
													color="grey"
													v-on="on"
												>
													mdi-lock
												</v-icon>
											</template>
											<span>
												Viewing example records requires "Can See PII Data" permission
											</span>
										</v-tooltip>
									</div>
								</v-expansion-panel-header>

								<v-expansion-panel-content v-if="canSeePiiData">
									<div
										v-if="rejection.exampleRecords && rejection.exampleRecords.length"
										class="pt-2"
									>
										<div
											v-for="(example, exIdx) in rejection.exampleRecords"
											:key="exIdx"
											class="example-record mb-3"
										>
											<div class="text-caption font-weight-bold text--secondary mb-2">
												Example {{ exIdx + 1 }}
											</div>
											<div class="example-fields">
												<div
													v-for="(value, field) in example"
													:key="field"
													class="d-flex mb-1"
												>
													<span class="field-key text-body-2 font-weight-medium">
														{{ field }}:
													</span>
													<span class="field-value text-body-2 text--secondary ml-2">
														{{ value || '(empty)' }}
													</span>
												</div>
											</div>
										</div>
									</div>
									<div
										v-else
										class="text-center text--secondary text-body-2 py-4"
									>
										No example records available
									</div>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
						<div
							v-if="hasMoreRejections"
							class="text-center mt-4"
						>
							<v-btn
								small
								text
								color="primary"
								@click="showMoreRejections"
							>
								Show more ({{ sortedRejections.length - rejectionsVisible }} remaining)
							</v-btn>
						</div>
					</v-card-text>
				</v-card>

				<!-- Rejection Details Not Available State -->
				<v-alert
					v-else-if="hasRejections && !hasRejectionDetails"
					type="info"
					outlined
					class="ma-4"
				>
					Rejection details are not available for this file.
				</v-alert>

				<!-- No Rejections State -->
				<v-alert
					v-else-if="!hasRejections"
					type="success"
					outlined
					class="ma-4"
				>
					All records were successfully processed with no rejections.
				</v-alert>
			</div>
		</template>
	</sliding-sheet>
</template>

<script>
import { mapGetters } from 'vuex'
import { SlidingSheet, StatusBadge } from '../../../components/component-library-wrapper.js'
import { getFileLoaderJob } from '../../../../../shared/utils/api/file-loader.js'
import { FILE_LOADER_CAN_SEE_PII } from '../../../../../shared/permissions/admin-portal-permissions.js'

export default {
	name: 'file-loader-details-sheet',

	components: {
		SlidingSheet,
		StatusBadge
	},

	props: {
		isOpen: {
			type: Boolean,
			required: true
		},
		fileLoaderGuid: {
			type: String,
			default: null
		}
	},

	data () {
		return {
			fileDetails: null,
			loading: false,
			error: false,
			rejectionsPageSize: 20,
			rejectionsVisible: 20
		}
	},

	computed: {
		...mapGetters('auth', ['productAreaPermission']),

		canSeePiiData () {
			return this.productAreaPermission(FILE_LOADER_CAN_SEE_PII)
		},

		fileName () {
			const fullPath = this.fileDetails?.fileName
			if (!fullPath) return 'File Details'
			const parts = fullPath.split(/[\\/]/)
			return parts[parts.length - 1] || fullPath
		},

		hasRejections () {
			return this.fileDetails?.rowsRejected > 0
		},

		hasRejectionDetails () {
			return this.fileDetails?.rejectionDetails?.length > 0
		},

		sortedRejections () {
			if (!this.fileDetails?.rejectionDetails) return []
			return [...this.fileDetails.rejectionDetails].sort((a, b) => b.rejectionCount - a.rejectionCount)
		},

		visibleRejections () {
			return this.sortedRejections.slice(0, this.rejectionsVisible)
		},

		hasMoreRejections () {
			return this.rejectionsVisible < this.sortedRejections.length
		},

		uniqueErrorCount () {
			return this.fileDetails?.rejectionDetails?.length || 0
		},

		derivedStatus () {
			if (!this.fileDetails) return { label: 'Unknown', type: 'info' }
			if (this.fileDetails.loadStatus?.toLowerCase() !== 'success') {
				return { label: 'Processing Failed', type: 'error' }
			}
			if (this.fileDetails.rowsRejected > 0 && this.fileDetails.rowsAccepted > 0) {
				return { label: 'Processed - Partially Accepted', type: 'warning' }
			}
			if (this.fileDetails.rowsRejected > 0 && this.fileDetails.rowsAccepted === 0) {
				return { label: 'Processed - All Rejected', type: 'error' }
			}
			return { label: 'Processed - All Accepted', type: 'success' }
		}
	},

	watch: {
		isOpen: {
			handler (newVal) {
				if (newVal && this.fileLoaderGuid) {
					this.loadFileDetails()
				} else if (!newVal) {
					this.resetState()
				}
			},
			immediate: true
		}
	},

	methods: {
		async loadFileDetails () {
			if (!this.fileLoaderGuid) return

			this.loading = true
			this.error = false

			try {
				const response = await getFileLoaderJob(this.fileLoaderGuid)
				this.fileDetails = response.data
			} catch (err) {
				console.error('Failed to load file details:', err)
				this.error = true
			} finally {
				this.loading = false
			}
		},

		showMoreRejections () {
			this.rejectionsVisible += this.rejectionsPageSize
		},

		formatDuration (seconds) {
			if (!seconds) return 'N/A'
			const mins = Math.floor(seconds / 60)
			const secs = seconds % 60
			return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
		},

		resetState () {
			this.fileDetails = null
			this.loading = false
			this.error = false
			this.rejectionsVisible = this.rejectionsPageSize
		}
	}
}
</script>

<style scoped>
.rejection-panels >>> .v-expansion-panel {
	margin-bottom: 12px;
	border-radius: 4px;
	overflow: hidden;
}

.rejection-panels >>> .v-expansion-panel::before {
	box-shadow: none;
}

.rejection-panels >>> .v-expansion-panel:last-child {
	margin-bottom: 0;
}

.example-record {
	background: var(--global-main-background-color, #f8f9fa);
	padding: 12px;
	border-radius: 4px;
}

.field-key {
	min-width: 140px;
}

.field-value {
	font-family: 'Courier New', monospace;
	word-break: break-word;
}

.rejects-filename-suffix {
	font-family: 'Courier New', monospace;
	font-size: 0.85em;
	padding: 2px 6px;
	border-radius: 3px;
	background-color: rgba(0, 0, 0, 0.06);
}

.dark .rejects-filename-suffix {
	background-color: rgba(255, 255, 255, 0.1);
}

</style>
